import React from 'react';
import { ShoppingCart, User as UserIcon, LogOut, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';
import { LOGO_URL } from '../constants';

export const Header: React.FC = () => {
  const { setIsCartOpen, cartCount } = useCart();
  const navigate = useNavigate();
  const { user, setIsLoginOpen, logout } = useAuth();

  return (
    <header className="bg-white/95 backdrop-blur text-stone-900 sticky top-0 z-50 shadow-sm border-b border-stone-200">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4 h-20">
        
        {/* Large Logo & Brand */}
        <div 
          onClick={() => navigate('/')}
          className="flex items-center gap-3 md:gap-4 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0 group"
        >
          <img 
            src={LOGO_URL} 
            alt="Devine stone" 
            className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full border border-stone-200 group-hover:border-stone-300 transition-colors shadow-sm" 
          />
          <span className="text-base sm:text-lg md:text-xl font-serif font-medium tracking-tight">
            Devine<span className="text-orange-600">stone</span>
          </span>
        </div>

        {/* Right Actions */}
        <div className="flex items-center space-x-2 sm:space-x-4 md:space-x-8">
          <div className="cursor-pointer group relative">
             <button 
                onClick={() => !user && setIsLoginOpen(true)}
                className="flex flex-col items-start leading-tight p-1"
             >
               <span className="text-[10px] uppercase tracking-wider text-stone-500 font-medium">Account</span>
               <span className="text-sm font-semibold flex items-center gap-1.5 text-stone-800">
                 {user ? (user.firstName || 'Profile') : 'Sign In'} <UserIcon size={14} className="hidden sm:block text-stone-400" />
               </span>
             </button>
             
             {user && (
               <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-stone-100 rounded-xl shadow-xl py-2 hidden group-hover:block animate-[fadeIn_0.2s_ease-out]">
                 <div className="px-5 py-3 border-b border-stone-100 mb-2 bg-stone-50/50">
                   <p className="text-[10px] uppercase font-semibold tracking-wider text-stone-500 mb-1">Signed in as</p>
                   {/* FIX: Now prioritizes Email as the primary identity display */}
                   <p className="text-sm font-medium truncate text-stone-900">{user.email || user.mobile || 'User'}</p>
                 </div>
                 <button 
                   onClick={() => navigate('/profile')}
                   className="w-full text-left px-5 py-3 text-sm hover:bg-stone-50 flex items-center gap-3 text-stone-700 hover:text-orange-600 transition-colors font-medium"
                 >
                   <UserIcon size={16} /> My Profile
                 </button>
                 <button 
                   onClick={() => navigate('/orders')}
                   className="w-full text-left px-5 py-3 text-sm hover:bg-stone-50 flex items-center gap-3 text-stone-700 hover:text-orange-600 transition-colors font-medium"
                 >
                   <Package size={16} /> My Orders
                 </button>
                 <button 
                   onClick={logout}
                   className="w-full text-left px-5 py-3 text-sm hover:bg-red-50 flex items-center gap-3 text-red-600 hover:text-red-700 transition-colors font-medium"
                 >
                   <LogOut size={16} /> Sign Out
                 </button>
               </div>
             )}
          </div>

          <div 
            onClick={() => setIsCartOpen(true)}
            className="flex items-center cursor-pointer p-2.5 relative bg-stone-50 hover:bg-stone-100 rounded-full transition-colors border border-stone-200"
          >
            <ShoppingCart size={20} className="text-stone-700" />
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white shadow-sm">
              {cartCount}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
};
